// Review workspace (PLAN §6, task 18): the three review steps, step 1 entity decisions staged on candidates, how
// candidates are grouped (by template block for profile sheets, by section for free documents), and how the
// source viewer cuts the normalized text into highlighted evidence spans. Pure functions: the API, the web app
// and the tests share them.

import { NAME_KINDS, type NameKind } from './claims';
import type { EntityKind } from './schema';

export const REVIEW_STEPS = [1, 2, 3] as const;
export type ReviewStep = (typeof REVIEW_STEPS)[number];

export const REVIEW_STEP_LABELS: Record<ReviewStep, string> = {
  1: 'Entity check',
  2: 'Facts & relations',
  3: 'AI re-check & conflicts',
};

export const REVIEW_TASK_STATUSES = ['open', 'in_progress', 'done'] as const;
export type ReviewTaskStatus = (typeof REVIEW_TASK_STATUSES)[number];

/** Pipeline statuses whose documents wait in the review queue: "review now" and "review later" uploads. */
export const REVIEWABLE_STATUSES = ['in_review', 'extracted'] as const;

// ── Step 1 decisions ──────────────────────────────────────────────────────────────────────────────────────

export interface ReviewName {
  value: string;
  /** ISO 639 code when known ("ar", "fr"); null = not said. */
  lang: string | null;
  kind: NameKind;
}

/**
 * What the reviewer decided for one extracted entity. Nothing is written to the graph in step 1: the decision is
 * staged on the candidate and committed with the document's claims when review completes.
 * - `new`: create a new entity of this type with these names (the first `primary` name is the display name).
 * - `match`: it is this existing entity (a cross-script or cross-language match needs `crossScriptConfirmed`).
 * - `same_as`: a duplicate of another candidate in the same document (merged into it: its names become aliases).
 * - `reject`: not an entity worth keeping (a misread, a generic word); its claims are dropped in step 2.
 */
export type EntityResolution =
  | { action: 'new'; typeKey: string; names: ReviewName[] }
  | { action: 'match'; entityId: string; crossScriptConfirmed?: boolean }
  | { action: 'same_as'; candidateId: string }
  | { action: 'reject' };

export type ResolutionAction = EntityResolution['action'];

const LANG = /^[a-z]{2,3}$/;
const MAX_NAMES = 30;
const MAX_NAME = 300;

/** Validates a decision sent by the client (shape only; the API checks ids, types and kinds against the data). */
export function parseResolution(input: unknown): { ok: true; value: EntityResolution } | { ok: false; error: string } {
  if (!input || typeof input !== 'object') return { ok: false, error: 'Send a decision.' };
  const b = input as Record<string, unknown>;
  switch (b.action) {
    case 'new': {
      if (typeof b.typeKey !== 'string' || !b.typeKey) return { ok: false, error: 'Pick a type.' };
      if (!Array.isArray(b.names)) return { ok: false, error: 'Give at least one name.' };
      const names: ReviewName[] = [];
      const seen = new Set<string>();
      for (const n of b.names.slice(0, MAX_NAMES)) {
        const o = (n ?? {}) as Record<string, unknown>;
        const value = typeof o.value === 'string' ? o.value.trim().replace(/\s+/g, ' ') : '';
        if (!value) continue;
        if (value.length > MAX_NAME) return { ok: false, error: `A name is longer than ${MAX_NAME} characters.` };
        if (seen.has(value)) continue;
        seen.add(value);
        const lang = typeof o.lang === 'string' && LANG.test(o.lang) ? o.lang : null;
        const kind = NAME_KINDS.includes(o.kind as NameKind) ? (o.kind as NameKind) : 'alias';
        names.push({ value, lang, kind });
      }
      if (!names.length) return { ok: false, error: 'Give at least one name.' };
      // Exactly one primary name: the first one marked primary, else the first name.
      const primary = Math.max(
        0,
        names.findIndex(n => n.kind === 'primary'),
      );
      return {
        ok: true,
        value: {
          action: 'new',
          typeKey: b.typeKey,
          names: names.map((n, i) => ({ ...n, kind: i === primary ? 'primary' : n.kind === 'primary' ? 'alias' : n.kind })),
        },
      };
    }
    case 'match':
      if (typeof b.entityId !== 'string' || !b.entityId) return { ok: false, error: 'Pick an existing entity.' };
      return { ok: true, value: { action: 'match', entityId: b.entityId, crossScriptConfirmed: b.crossScriptConfirmed === true } };
    case 'same_as':
      if (typeof b.candidateId !== 'string' || !b.candidateId) return { ok: false, error: 'Pick the entity this one duplicates.' };
      return { ok: true, value: { action: 'same_as', candidateId: b.candidateId } };
    case 'reject':
      return { ok: true, value: { action: 'reject' } };
    default:
      return { ok: false, error: 'Unknown decision.' };
  }
}

/** The `candidates.decision` value a step 1 decision is stored as (PLAN §4). */
export function decisionOf(r: EntityResolution, edited: boolean): 'accepted' | 'edited' | 'rejected' | 'merged' {
  if (r.action === 'reject') return 'rejected';
  if (r.action === 'new') return edited ? 'edited' : 'accepted';
  return 'merged';
}

/**
 * Follows `same_as` links from a candidate to the candidate that stands for the entity. `cycle` is true when the
 * links loop back (the API refuses a decision that would create one).
 */
export function sameAsRoot(resolutions: ReadonlyMap<string, EntityResolution | null>, id: string): { rootId: string; cycle: boolean } {
  const seen = new Set<string>([id]);
  let cur = id;
  for (;;) {
    const r = resolutions.get(cur);
    if (r?.action !== 'same_as') return { rootId: cur, cycle: false };
    if (seen.has(r.candidateId)) return { rootId: cur, cycle: true };
    seen.add(r.candidateId);
    cur = r.candidateId;
  }
}

// ── Types and names from an extracted entity ──────────────────────────────────────────────────────────────

type TypeLike = { key: string; label: string; kind: EntityKind; parentTypeId: string | null; id: string; archived?: boolean };

const fold = (s: string) =>
  s
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[_\s/-]+/g, ' ')
    .trim();

const TYPE_SYNONYMS: Record<string, string> = {
  individual: 'person',
  people: 'person',
  politician: 'person',
  journalist: 'person',
  organisation: 'organization',
  association: 'ngo',
  'think tank': 'think_tank',
  'media outlet': 'media',
  newspaper: 'media',
  'political party': 'party',
  government: 'government_body',
  ministry: 'government_body',
  location: 'place',
  transaction: 'asset',
  // Harm events (D398): their own names inside longer wording ("Surveillance operation"), and common wording.
  'legal action': 'legal_action',
  surveillance: 'surveillance',
  'online interference': 'online_interference',
  lawsuit: 'legal_action',
  lawfare: 'legal_action',
  'legal case': 'legal_action',
  'legal threat': 'legal_action',
  prosecution: 'legal_action',
  complaint: 'legal_action',
  spyware: 'surveillance',
  hacking: 'surveillance',
  monitoring: 'surveillance',
  infiltration: 'surveillance',
  disinformation: 'online_interference',
  'smear campaign': 'online_interference',
  'harassment campaign': 'online_interference',
  'online harassment': 'online_interference',
  'bot network': 'online_interference',
  interference: 'online_interference',
};

/**
 * The entity type an extracted type string ("Person", "Think tank / Advocacy Organisation", "organisation") most
 * likely means, among the non-archived types of its kind; the kind's root type when nothing fits.
 */
export function matchTypeKey(types: readonly TypeLike[], kind: EntityKind, typeText: string | null | undefined): string | null {
  const ofKind = types.filter(t => t.kind === kind && !t.archived);
  const root = ofKind.find(t => !t.parentTypeId) ?? ofKind[0];
  const text = fold(typeText ?? '');
  if (!text) return root?.key ?? null;
  const byWords = (s: string) => ofKind.find(t => fold(t.label) === s || fold(t.key) === s) ?? ofKind.find(t => t.key === TYPE_SYNONYMS[s]);
  const exact = byWords(text);
  if (exact) return exact.key;
  // "Think tank / Advocacy Organisation": the first part that names a type.
  for (const part of (typeText ?? '').split(/[/,;()|]+/)) {
    const hit = byWords(fold(part));
    if (hit) return hit.key;
  }
  // A known label inside the text ("NGO / association" → "ngo"); longest label first.
  const inside = [...ofKind]
    .sort((a, b) => b.label.length - a.label.length)
    .find(t => fold(t.label).length > 2 && ` ${text} `.includes(` ${fold(t.label)} `));
  if (inside) return inside.key;
  // A common wording inside the text ("Defamation lawsuit", "Coordinated smear campaign"); longest first.
  const syn = Object.keys(TYPE_SYNONYMS)
    .filter(k => ` ${text} `.includes(` ${k} `))
    .sort((a, b) => b.length - a.length)
    .map(k => ofKind.find(t => t.key === TYPE_SYNONYMS[k]))
    .find(Boolean);
  return syn?.key ?? root?.key ?? null;
}

/** An extracted entity as the extraction stored it (only what step 1 reads). */
export interface ExtractedEntityLike {
  name: string;
  aliases?: string[];
  asWritten?: string[];
  transliteration?: string | null;
}

/** Names for a new entity from an extracted one: its name (primary), other spellings (aliases), transliteration. */
export function namesFromExtracted(e: ExtractedEntityLike, lang: string | null = null): ReviewName[] {
  const out: ReviewName[] = [];
  const seen = new Set<string>();
  const add = (value: string | null | undefined, kind: NameKind) => {
    const v = (value ?? '').trim().replace(/\s+/g, ' ');
    if (!v || seen.has(v) || v.length > MAX_NAME) return;
    seen.add(v);
    out.push({ value: v, lang: kind === 'primary' ? lang : null, kind });
  };
  add(e.name, 'primary');
  for (const a of e.aliases ?? []) add(a, 'alias');
  for (const a of e.asWritten ?? []) add(a, 'alias');
  add(e.transliteration, 'transliteration');
  return out.slice(0, MAX_NAMES);
}

// ── Grouping by block / section ───────────────────────────────────────────────────────────────────────────

export interface ReviewSection {
  key: string;
  label: string;
  /** Character offset in the normalized text where the section starts. */
  start: number;
}

export const WHOLE_DOCUMENT: ReviewSection = { key: 'document', label: 'Whole document', start: 0 };

/**
 * Sections of a free document: its Markdown headings (`#`…`######`), with "Beginning" for text before the first
 * heading; one "Whole document" section when it has none.
 */
export function headingSections(text: string): ReviewSection[] {
  const out: ReviewSection[] = [];
  let offset = 0;
  const used = new Map<string, number>();
  for (const line of text.split('\n')) {
    const h = /^\s{0,3}#{1,6}\s+(.+?)\s*#*\s*$/.exec(line);
    if (h?.[1]) {
      const label =
        h[1]
          .replace(/[*_`\\]/g, '')
          .trim()
          .slice(0, 120) || 'Section';
      const base = `h:${label.toLowerCase()}`;
      const n = (used.get(base) ?? 0) + 1;
      used.set(base, n);
      out.push({ key: n > 1 ? `${base}#${n}` : base, label, start: offset });
    }
    offset += line.length + 1;
  }
  if (!out.length) return [WHOLE_DOCUMENT];
  if (out[0] && out[0].start > 0 && text.slice(0, out[0].start).trim()) out.unshift({ key: 'start', label: 'Beginning', start: 0 });
  return out;
}

/**
 * Sections of a profile sheet: its template blocks (Header, Block 1 · Identification…), each starting where the
 * template importer found its first item. Blocks the sheet doesn't have are left out.
 */
export function sheetSections(
  blocks: readonly { key: string; label: string; number: number | null }[],
  items: readonly { block: string; locator: { charStart: number } }[],
): ReviewSection[] {
  const starts = new Map<string, number>();
  for (const i of items) {
    const s = starts.get(i.block);
    if (s === undefined || i.locator.charStart < s) starts.set(i.block, i.locator.charStart);
  }
  const out: ReviewSection[] = [];
  for (const b of blocks) {
    const start = b.key === 'header' ? 0 : starts.get(b.key);
    if (start === undefined) continue;
    out.push({ key: `block:${b.key}`, label: b.number ? `Block ${b.number} · ${b.label}` : b.label, start });
  }
  out.sort((a, b) => a.start - b.start);
  if (!out.length) return [WHOLE_DOCUMENT];
  if (out[0] && out[0].start > 0) out.unshift({ key: 'block:header', label: 'Header', start: 0 });
  return out;
}

/** The section a character offset falls in (the last one starting at or before it). */
export function sectionAt(sections: readonly ReviewSection[], pos: number): ReviewSection {
  let hit = sections[0] ?? WHOLE_DOCUMENT;
  for (const s of sections) if (s.start <= pos) hit = s;
  return hit;
}

export const NOT_LOCATED: ReviewSection = { key: 'none', label: 'Not located in the text', start: Number.MAX_SAFE_INTEGER };

/**
 * Groups items by the section of their first evidence position, sections in document order and items by position
 * within each. Items with no position go last, under "Not located in the text". Empty sections are left out.
 */
export function groupBySection<T>(
  items: readonly T[],
  positionOf: (item: T) => number | null,
  sections: readonly ReviewSection[],
): { section: ReviewSection; items: T[] }[] {
  const groups = new Map<string, { section: ReviewSection; items: { item: T; pos: number }[] }>();
  for (const item of items) {
    const pos = positionOf(item);
    const section = pos === null ? NOT_LOCATED : sectionAt(sections, pos);
    const g = groups.get(section.key) ?? { section, items: [] };
    g.items.push({ item, pos: pos ?? Number.MAX_SAFE_INTEGER });
    groups.set(section.key, g);
  }
  return [...groups.values()]
    .sort((a, b) => a.section.start - b.section.start)
    .map(g => ({ section: g.section, items: g.items.sort((a, b) => a.pos - b.pos).map(x => x.item) }));
}

// ── Source viewer: evidence highlighting ──────────────────────────────────────────────────────────────────

export interface TextSpan {
  /** Who the span belongs to (a candidate id). */
  id: string;
  start: number;
  end: number;
}

export interface TextSegment {
  start: number;
  end: number;
  text: string;
  /** Spans covering this segment (empty = plain text). */
  ids: string[];
}

export interface TextLineView {
  /** 0-based line number. */
  n: number;
  start: number;
  segments: TextSegment[];
}

/**
 * Cuts the text into lines, and each line into segments at span boundaries, so a viewer can render every line
 * with its own direction (`dir=auto`, Arabic and Latin lines side by side) and mark the evidence spans. Spans are
 * clipped to the text; a span across a line break is marked on both lines.
 */
export function segmentText(text: string, spans: readonly TextSpan[]): TextLineView[] {
  const clean = spans
    .map(s => ({ id: s.id, start: Math.max(0, Math.min(text.length, s.start)), end: Math.max(0, Math.min(text.length, s.end)) }))
    .filter(s => s.end > s.start)
    .sort((a, b) => a.start - b.start);
  const lines: TextLineView[] = [];
  let offset = 0;
  let from = 0; // first span that may still overlap the current line
  for (const [n, raw] of text.split('\n').entries()) {
    const start = offset;
    const end = offset + raw.length;
    offset = end + 1;
    while (from < clean.length && (clean[from]?.end ?? 0) <= start) from++;
    const here: { id: string; start: number; end: number }[] = [];
    for (let i = from; i < clean.length; i++) {
      const s = clean[i];
      if (!s || s.start >= end) break;
      if (s.end > start) here.push(s);
    }
    if (!here.length) {
      lines.push({ n, start, segments: raw ? [{ start, end, text: raw, ids: [] }] : [] });
      continue;
    }
    const cuts = new Set<number>([start, end]);
    for (const s of here) {
      cuts.add(Math.max(start, s.start));
      cuts.add(Math.min(end, s.end));
    }
    const points = [...cuts].sort((a, b) => a - b);
    const segments: TextSegment[] = [];
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i] as number;
      const b = points[i + 1] as number;
      if (b <= a) continue;
      segments.push({ start: a, end: b, text: text.slice(a, b), ids: here.filter(s => s.start < b && s.end > a).map(s => s.id) });
    }
    lines.push({ n, start, segments });
  }
  return lines;
}
